<template>
  <v-container class="wbp-page">
    <div class="page-header">
      <h2>Welcome Bag Packing</h2>
      <div>
        <label class="filter-toggle">
          <input type="checkbox" v-model="onsiteOnly" />
          Only onsite + room-share dancers
        </label>
        <label class="filter-toggle">
          <input type="checkbox" v-model="earlyOnly" />
          Only early arrivals (Jul 3–5)
        </label>
        <label class="filter-toggle">
          <input type="checkbox" v-model="hidePacked" />
          Hide fully-packed
        </label>
        <label class="filter-toggle">
          <input type="checkbox" v-model="missingOnly" />
          Only bags with missing items
        </label>
      </div>
    </div>

    <div class="summary-bar">
      <div class="summary-stat">
        <span class="stat-num">{{ visibleDancers.length }}</span>
        <span class="stat-label">bags to pack</span>
      </div>
      <div class="summary-stat">
        <span class="stat-num">{{ fullyPackedCount }}</span>
        <span class="stat-label">fully packed</span>
      </div>
      <div class="summary-stat">
        <span class="stat-num">{{ totalItems }}</span>
        <span class="stat-label">total items</span>
      </div>
      <div class="summary-stat">
        <span class="stat-num">{{ checkedItemCount }}</span>
        <span class="stat-label">packed</span>
      </div>
      <div class="summary-stat" v-if="missingItemCount > 0">
        <span class="stat-num stat-missing">{{ missingItemCount }}</span>
        <span class="stat-label">flagged missing</span>
      </div>
    </div>

    <div v-if="loading" class="muted">Loading…</div>
    <div v-else-if="visibleDancers.length === 0" class="muted">No dancers match the current filter.</div>
    <div v-else class="packing-grid">
      <section
        v-for="d in visibleDancers"
        :key="d.id"
        class="pack-card"
        :class="{ 'is-done': isDancerFullyPacked(d) && !dancerHasMissing(d), 'has-missing': dancerHasMissing(d) }"
      >
        <header class="pack-head">
          <div class="pack-name">{{ d.name }}</div>
          <div class="pack-badges">
            <span class="chip" :class="'chip-' + d.locationClass">{{ d.locationLabel }}</span>
            <span v-if="d.nights !== null" class="chip chip-nights">{{ d.nights }} night{{ d.nights === 1 ? '' : 's' }}</span>
            <span v-if="d.earlyArrival" class="chip chip-early">Early: {{ d.earlyArrivalDay }}</span>
            <span v-if="d.inSoloOrDuet" class="chip chip-comp">Solo/Duet</span>
            <span v-if="dancerHasMissing(d)" class="chip chip-missing">⚠ {{ dancerMissingCount(d) }} missing</span>
          </div>
        </header>
        <div class="pack-section pack-section-personal">
          <div class="pack-section-head">Just for {{ d.name.split(' ')[0] }}</div>
          <ul class="pack-items">
            <li v-for="item in personalItems(d)" :key="item.key" class="pack-item" :class="'pack-item-' + statusFor(d.id, item.key)">
              <div class="pack-row">
                <div class="pack-toggle" v-if="!item.note">
                  <button
                    type="button"
                    class="tog tog-packed"
                    :class="{ 'is-on': statusFor(d.id, item.key) === 'packed' }"
                    :disabled="saving"
                    :title="statusFor(d.id, item.key) === 'packed' ? 'Click to unmark' : 'Mark packed'"
                    @click="setStatus(d.id, item.key, 'packed')"
                  >✓</button>
                  <button
                    type="button"
                    class="tog tog-missing"
                    :class="{ 'is-on': statusFor(d.id, item.key) === 'missing' }"
                    :disabled="saving"
                    :title="statusFor(d.id, item.key) === 'missing' ? 'Click to clear' : 'Flag missing'"
                    @click="setStatus(d.id, item.key, 'missing')"
                  >⚠</button>
                </div>
                <div class="pack-line" :class="{ 'is-note': item.note }">
                  <span class="pack-item-label">{{ item.label }}</span>
                  <span v-if="item.detail" class="pack-item-detail">{{ item.detail }}</span>
                </div>
              </div>
            </li>
            <li v-if="personalItems(d).length === 0" class="pack-item muted small">
              (nothing personal to add — just the universal items below)
            </li>
          </ul>
        </div>
        <details class="pack-section pack-section-universal" open>
          <summary class="pack-section-head">Every bag ({{ universalItems(d).length }} items)</summary>
          <ul class="pack-items">
            <li v-for="item in universalItems(d)" :key="item.key" class="pack-item" :class="'pack-item-' + statusFor(d.id, item.key)">
              <div class="pack-row">
                <div class="pack-toggle">
                  <button
                    type="button"
                    class="tog tog-packed"
                    :class="{ 'is-on': statusFor(d.id, item.key) === 'packed' }"
                    :disabled="saving"
                    @click="setStatus(d.id, item.key, 'packed')"
                  >✓</button>
                  <button
                    type="button"
                    class="tog tog-missing"
                    :class="{ 'is-on': statusFor(d.id, item.key) === 'missing' }"
                    :disabled="saving"
                    @click="setStatus(d.id, item.key, 'missing')"
                  >⚠</button>
                </div>
                <div class="pack-line">
                  <span class="pack-item-label">{{ item.label }}</span>
                </div>
              </div>
            </li>
          </ul>
        </details>
      </section>
    </div>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { computeNights } from '@/utils/lodgingDates'


const PACKING_DOC = 'welcome-bag-packing'

// Merch orders placed under one name that should be routed to another dancer's
// welcome bag. Keys are lower-cased order dancerName (or dancerEmail); values
// are the target dancer's email. Used when family / group orders come in
// under a non-dancer name but need to end up in one dancer's bag.
const MERCH_REDIRECTS = {
  'perri gellman': 'hello@brocheballet.com',
  'bob gellman': 'hello@brocheballet.com'
}

export default {
  mixins: [iabfAdminMixin],
  name: 'WelcomeBagPacking',
  data () {
    return {
      loading: true,
      orders: [],
      checked: {},
      saving: false,
      onsiteOnly: true,
      earlyOnly: false,
      hidePacked: false,
      missingOnly: false,
      unsubPacking: null
    }
  },
  computed: {
    allDancers () {
      return this.$store.getters.loadedIABFDancers || []
    },
    allPieces () {
      return this.$store.getters.loadedIABFPieces || []
    },
    travelByPersonId () {
      const map = new Map()
      for (const td of (this.$store.getters.loadedAllTravelDetails || [])) {
        if (td.personId) map.set(td.personId, td)
      }
      return map
    },
    allGuestRegs () {
      return this.$store.getters.allGuestRegistrations || []
    },
    guestsByDancerId () {
      // Regular guests (type 'guest' or missing) attached to a dancer.
      const map = new Map()
      for (const g of this.allGuestRegs) {
        if (g.type && g.type !== 'guest') continue
        const dancerId = g.dancerId
        if (!dancerId) continue
        if (!map.has(dancerId)) map.set(dancerId, [])
        map.get(dancerId).push(g)
      }
      return map
    },
    guestsByFacultyId () {
      // Faculty guests attached to a faculty person.
      const map = new Map()
      for (const g of this.allGuestRegs) {
        if (g.type !== 'faculty_guest') continue
        const facultyId = g.dancerId
        if (!facultyId) continue
        if (!map.has(facultyId)) map.set(facultyId, [])
        map.get(facultyId).push(g)
      }
      return map
    },
    facultyList () {
      return this.allGuestRegs.filter(g => g.type === 'faculty')
    },
    facultyOrderFirstNameSet () {
      // First names from paid orders flagged isFaculty at checkout.
      const set = new Set()
      for (const o of this.orders) {
        if (o.status !== 'paid') continue
        if (!o.isFaculty) continue
        const n = (o.dancerName || '').toLowerCase().replace(/\s+/g, ' ').trim()
        if (!n) continue
        const first = n.split(/\s+/)[0]
        if (first) set.add(first)
      }
      return set
    },
    soloOrDuetDancerIds () {
      // Dancers who appear in any Solo or Duet piece — used to decide whether
      // their program insert needs a competition pamphlet.
      const set = new Set()
      for (const p of this.allPieces) {
        const gs = (p.groupSize || '').toLowerCase()
        const isSoloDuet = gs === 'solo' || gs.startsWith('duet')
        if (!isSoloDuet) continue
        const dancers = Array.isArray(p.dancers) ? p.dancers : []
        for (const id of dancers) set.add(id)
      }
      return set
    },
    ordersByEmail () {
      // Paid orders, keyed by lowercased dancer email. A dancer might have
      // multiple paid orders (original + add-ons) — merge their lines.
      // MERCH_REDIRECTS lets us route a non-dancer's order to another dancer's
      // bag (e.g. family/group orders under one shopper's name).
      const map = new Map()
      const push = (email, line) => {
        if (!email) return
        if (!map.has(email)) map.set(email, [])
        map.get(email).push(line)
      }
      for (const o of this.orders) {
        if (o.status !== 'paid') continue
        const email = (o.dancerEmail || '').toLowerCase().trim()
        const name = (o.dancerName || '').toLowerCase().replace(/\s+/g, ' ').trim()
        const redirectTarget = MERCH_REDIRECTS[name] || MERCH_REDIRECTS[email] || null
        const destinationEmail = redirectTarget || email
        for (const line of (o.lines || [])) {
          push(destinationEmail, line)
        }
      }
      return map
    },
    dancerRows () {
      return this.allDancers
        .map(d => this.buildDancerRow(d))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    facultyRows () {
      return this.facultyList
        .map(f => this.buildFacultyRow(f))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    visibleDancers () {
      let list = [...this.dancerRows]
      if (this.onsiteOnly) list = list.filter(d => d.isOnsite)
      if (this.earlyOnly) {
        list = list.filter(d => d.earlyArrival)
      } else if (!this.onsiteOnly) {
        // Faculty are offsite by definition — only include them when the
        // onsite-only filter is off. Merge and re-sort so faculty are woven
        // into the alphabetical dancer list instead of tacked on at the end.
        list = [...list, ...this.facultyRows].sort((a, b) => a.name.localeCompare(b.name))
      }
      if (this.hidePacked) list = list.filter(d => !this.isDancerFullyPacked(d))
      if (this.missingOnly) list = list.filter(d => this.dancerHasMissing(d))
      return list
    },
    fullyPackedCount () {
      return this.visibleDancers.filter(d => this.isDancerFullyPacked(d)).length
    },
    totalItems () {
      return this.visibleDancers.reduce((n, d) => n + d.items.filter(i => !i.note).length, 0)
    },
    checkedItemCount () {
      let n = 0
      for (const d of this.visibleDancers) {
        for (const item of d.items) {
          if (item.note) continue
          if (this.isChecked(d.id, item.key)) n++
        }
      }
      return n
    },
    missingItemCount () {
      let n = 0
      for (const d of this.visibleDancers) {
        for (const item of d.items) {
          if (item.note) continue
          if (this.statusFor(d.id, item.key) === 'missing') n++
        }
      }
      return n
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    await Promise.all([
      this.$store.dispatch('loadIABFDancers'),
      this.$store.dispatch('loadIABFPieces', { useAdminYear: true }),
      this.$store.dispatch('loadAllTravelDetails'),
      this.$store.dispatch('loadAllGuestRegistrations')
    ])
    const snap = await db.collection(this.IABF.storeOrders).get()
    this.orders = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    this.subscribePacking()
    this.loading = false
  },
  beforeDestroy () {
    if (this.unsubPacking) this.unsubPacking()
  },
  methods: {
    buildDancerRow (d) {
      const name = [d.firstName, d.lastName].filter(Boolean).join(' ').trim() || d.email || '(no name)'
      const room = (d.room || '').trim()
      const isOffsite = !room || room === 'Staying offsite'
      const isSharing = room === 'Staying with another dancer'
      const isOnsite = !isOffsite
      const locationLabel = isOffsite
        ? 'Offsite'
        : isSharing ? 'Room-share' : room
      const locationClass = isOffsite ? 'off' : isSharing ? 'share' : 'on'

      // Nights staying — from dancer's own travel-details doc (falls back to
      // roommate's for room-shares who didn't submit their own). Only shown
      // for onsite/room-share; offsite dancers aren't sleeping on-property.
      // Also compute early-arrival status from extraNight1/2/3 (Jul 3/4/5).
      let nights = null
      let earlyArrival = false
      let earlyArrivalDay = ''
      if (isOnsite) {
        const td = this.travelByPersonId.get(d.id) ||
          (d.primaryRoomId ? this.travelByPersonId.get(d.primaryRoomId) : null)
        nights = computeNights(td)
        if (td) {
          if (td.extraNight1) { earlyArrival = true; earlyArrivalDay = 'Jul 3' }
          else if (td.extraNight2) { earlyArrival = true; earlyArrivalDay = 'Jul 4' }
          else if (td.extraNight3) { earlyArrival = true; earlyArrivalDay = 'Jul 5' }
        }
      }

      const items = []
      const inSoloOrDuet = this.soloOrDuetDancerIds.has(d.id)

      // Universal items — every bag gets these. Tagged so we render them in
      // a separate section from the per-dancer specifics.
      items.push({ key: 'sticker', label: 'Sticker', group: 'universal' })
      items.push({
        key: 'program',
        label: inSoloOrDuet ? 'Program + competition pamphlet' : 'Program',
        group: 'universal'
      })
      items.push({ key: 'tissues', label: 'Tissues', group: 'universal' })
      items.push({ key: 'dancerstrong-card', label: "Allie's DancerStrong card", group: 'universal' })
      items.push({ key: 'corps-leotard', label: 'Corps leotard', group: 'universal' })
      items.push({ key: 'handy-card', label: '"so you have it handy" card', group: 'universal' })

      // Onsite-only universals.
      if (isOnsite) {
        items.push({ key: 'toiletries', label: 'Toiletries', group: 'universal' })
        items.push({ key: 'makeup-wipes', label: 'Makeup wipes', group: 'universal' })
      }

      // Alice in Wonderland bag — only when in Alice AND not bringing their
      // own tutu from home. When they ARE bringing their own, show a red note
      // instead so the packer knows why the bag is skipped.
      const aiw = d.aliceInWonderland || null
      const inAlice = !!(aiw && aiw.participating)
      const bringingOwnAlice = inAlice && aiw.tutuChoice === 'own'
      if (inAlice && !bringingOwnAlice) {
        let suffix = ''
        if (aiw.role === 'Pointe') {
          if (aiw.tutuChoice === 'buy') suffix = ' — PANCAKE (buying)'
          else if (aiw.tutuChoice === 'borrow') suffix = ' — PANCAKE (borrowing)'
        } else if (aiw.role === 'Flat Ladies') {
          suffix = ' — romantic tutu'
        }
        items.push({ key: 'alice-bag', label: `Alice in Wonderland bag${suffix}`, group: 'personal' })
      }

      // Coffee/tea — only when onsite (dorm/cottage) or sharing a room.
      if (isOnsite && (d.coffee || '').trim()) {
        items.push({
          key: 'coffee',
          label: 'Coffee/Tea',
          detail: d.coffee.trim(),
          group: 'personal'
        })
      }

      // Hairdryer — onsite / room-share only when dancer opted in on lodging
      if (isOnsite && d.wantsHairdryer === true) {
        items.push({
          key: 'hairdryer',
          label: 'Hairdryer in room',
          group: 'personal'
        })
      }

      // Merch: one checkable line per SKU × size.
      const email = (d.email || '').toLowerCase().trim()
      const lines = this.ordersByEmail.get(email) || []
      const merged = new Map()
      for (const l of lines) {
        const skuKey = `${l.name || '(unnamed)'}::${l.size || ''}`
        if (!merged.has(skuKey)) {
          merged.set(skuKey, { name: l.name || '(unnamed)', size: l.size || '', quantity: 0 })
        }
        merged.get(skuKey).quantity += (l.quantity || 0)
      }
      const merchLines = Array.from(merged.values())
        .filter(m => m.quantity > 0)
        .sort((a, b) => a.name.localeCompare(b.name) || a.size.localeCompare(b.size))
      for (const m of merchLines) {
        items.push({
          key: `merch::${m.name}::${m.size}`,
          label: `${m.quantity}× ${m.name}${m.size ? ` (${m.size})` : ''}`,
          group: 'personal'
        })
      }

      // Nametags — the dancer's own first, then one per registered guest.
      items.push({ key: 'own-nametag', label: `Nametag: ${name}`, group: 'personal' })
      const guests = this.guestsByDancerId.get(d.id) || []
      for (const g of guests) {
        const gName = (g.guestName || '').trim() || '(unnamed guest)'
        items.push({
          key: `guest-nametag::${g.id}`,
          label: `Nametag: ${gName}`,
          group: 'personal'
        })
      }

      // Alice tutu-from-home note — informational red flag when they're
      // bringing their own tutu (Pointe or Flat Ladies with tutuChoice
      // 'own'). Reason the Alice bag is skipped.
      if (bringingOwnAlice) {
        const roleLabel = aiw.role === 'Pointe' ? 'PANCAKE' : 'romantic'
        items.push({
          key: 'alice-own-note',
          label: `NOTE: bringing own Alice ${roleLabel} tutu from home`,
          note: true,
          group: 'personal'
        })
      }

      return {
        id: d.id,
        kind: 'dancer',
        name,
        room,
        isOnsite,
        isSharing,
        locationLabel,
        locationClass,
        nights,
        earlyArrival,
        earlyArrivalDay,
        inSoloOrDuet,
        items
      }
    },
    buildFacultyRow (f) {
      const name = (f.guestName || '').trim() || f.email || '(unnamed faculty)'
      const items = []

      // Faculty bags get program + "so you have it handy" card. No sticker,
      // tissues, corps leotard, or DancerStrong card.
      items.push({ key: 'program', label: 'Program', group: 'universal' })
      items.push({ key: 'handy-card', label: '"so you have it handy" card', group: 'universal' })

      // Merch for faculty — match by lowercased email OR full name OR (as a
      // fallback) any paid order flagged isFaculty whose dancerName's first
      // name matches this faculty's first name.
      const email = (f.email || '').toLowerCase().trim()
      const nameKey = name.toLowerCase().replace(/\s+/g, ' ').trim()
      const firstName = nameKey.split(/\s+/)[0] || ''
      const lines = []
      for (const o of this.orders) {
        if (o.status !== 'paid') continue
        const oEmail = (o.dancerEmail || '').toLowerCase().trim()
        const oName = (o.dancerName || '').toLowerCase().replace(/\s+/g, ' ').trim()
        const oFirst = oName.split(/\s+/)[0] || ''
        const emailMatch = !!email && oEmail === email
        const nameMatch = !!nameKey && oName === nameKey
        const firstMatch = !!o.isFaculty && !!firstName && oFirst === firstName
        if (emailMatch || nameMatch || firstMatch) {
          for (const line of (o.lines || [])) lines.push(line)
        }
      }
      const merged = new Map()
      for (const l of lines) {
        const skuKey = `${l.name || '(unnamed)'}::${l.size || ''}`
        if (!merged.has(skuKey)) {
          merged.set(skuKey, { name: l.name || '(unnamed)', size: l.size || '', quantity: 0 })
        }
        merged.get(skuKey).quantity += (l.quantity || 0)
      }
      const merchLines = Array.from(merged.values())
        .filter(m => m.quantity > 0)
        .sort((a, b) => a.name.localeCompare(b.name) || a.size.localeCompare(b.size))
      for (const m of merchLines) {
        items.push({
          key: `merch::${m.name}::${m.size}`,
          label: `${m.quantity}× ${m.name}${m.size ? ` (${m.size})` : ''}`,
          group: 'personal'
        })
      }

      // Nametags — faculty's own first, then one per faculty_guest.
      items.push({ key: 'own-nametag', label: `Nametag: ${name}`, group: 'personal' })
      const guests = this.guestsByFacultyId.get(f.id) || []
      for (const g of guests) {
        const gName = (g.guestName || '').trim() || '(unnamed guest)'
        items.push({
          key: `guest-nametag::${g.id}`,
          label: `Nametag: ${gName}`,
          group: 'personal'
        })
      }

      return {
        id: `faculty::${f.id}`,
        kind: 'faculty',
        name,
        room: '',
        isOnsite: false,
        isSharing: false,
        locationLabel: 'Faculty · Offsite',
        locationClass: 'faculty',
        nights: null,
        earlyArrival: false,
        earlyArrivalDay: '',
        inSoloOrDuet: false,
        items
      }
    },
    personalItems (d) {
      return d.items.filter(i => i.group === 'personal')
    },
    universalItems (d) {
      return d.items.filter(i => i.group === 'universal')
    },
    dancerHasMissing (d) {
      return d.items.some(i => !i.note && this.statusFor(d.id, i.key) === 'missing')
    },
    dancerMissingCount (d) {
      let n = 0
      for (const i of d.items) {
        if (i.note) continue
        if (this.statusFor(d.id, i.key) === 'missing') n++
      }
      return n
    },
    isDancerFullyPacked (d) {
      // A bag is "fully packed" (i.e. can be hidden) when every packable
      // item has a status set — either 'packed' or 'missing'. Missing counts
      // because the packer has already made the decision on that item.
      const packable = d.items.filter(i => !i.note)
      if (packable.length === 0) return false
      return packable.every(i => {
        const s = this.statusFor(d.id, i.key)
        return s === 'packed' || s === 'missing'
      })
    },
    keyFor (dancerId, itemKey) {
      return `${dancerId}__${itemKey}`
    },
    statusFor (dancerId, itemKey) {
      // Legacy: `true` (from before MISSING) maps to 'packed'.
      const raw = this.checked[this.keyFor(dancerId, itemKey)]
      if (raw === true || raw === 'packed') return 'packed'
      if (raw === 'missing') return 'missing'
      return ''
    },
    isChecked (dancerId, itemKey) {
      // Kept for the fully-packed rollup — MISSING doesn't count as packed.
      return this.statusFor(dancerId, itemKey) === 'packed'
    },
    async setStatus (dancerId, itemKey, targetStatus) {
      // Toggling the same status clears back to pending; otherwise flip.
      const k = this.keyFor(dancerId, itemKey)
      const prev = this.checked[k]
      const current = this.statusFor(dancerId, itemKey)
      const next = current === targetStatus ? null : targetStatus
      if (next === null) {
        this.$delete(this.checked, k)
      } else {
        this.$set(this.checked, k, next)
      }
      this.saving = true
      try {
        await db.collection(this.IABF.config).doc(PACKING_DOC).set({
          checked: this.checked,
          updatedAt: new Date()
        }, { merge: true })
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Failed to save: ' + (e.message || e))
        if (prev === undefined) this.$delete(this.checked, k)
        else this.$set(this.checked, k, prev)
      }
      this.saving = false
    },
    subscribePacking () {
      const ref = db.collection(this.IABF.config).doc(PACKING_DOC)
      this.unsubPacking = ref.onSnapshot(snap => {
        if (!snap.exists) {
          this.checked = {}
          return
        }
        this.checked = (snap.data() && snap.data().checked) || {}
      })
    },
  }
}
</script>

<style scoped>
.wbp-page {
  max-width: 1400px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.filter-toggle {
  font-size: 13px;
  color: #555;
  margin-right: 12px;
  cursor: pointer;
  user-select: none;
}
.filter-toggle input {
  margin-right: 4px;
  vertical-align: middle;
}
.summary-bar {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  background: #fbfaf6;
  border: 1px solid #eee5d2;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 18px;
}
.summary-stat {
  display: flex;
  flex-direction: column;
  min-width: 90px;
}
.stat-num {
  font-size: 22px;
  font-weight: 700;
  color: #267E7D;
  line-height: 1;
}
.stat-num.stat-missing { color: #b73a2b; }
.stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #888;
  margin-top: 4px;
}
.muted {
  color: #888;
}
.small {
  font-size: 12px;
}
.packing-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
@media (max-width: 900px) {
  .packing-grid { grid-template-columns: 1fr; }
}
.pack-card {
  background: #fff;
  border: 1px solid #e6e0d0;
  border-radius: 8px;
  padding: 12px 14px;
  break-inside: avoid;
  transition: background .18s, border-color .18s;
}
.pack-card.is-done {
  background: #f4faf3;
  border-color: #bfe5b3;
}
.pack-card.has-missing {
  background: #fdf4f2;
  border-color: #f0b0a2;
  box-shadow: 0 0 0 1px #f0b0a2 inset;
}
.chip-missing { background: #fbd6cf; color: #8a1e10; }
.pack-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #eee5d2;
}
.pack-name {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}
.pack-badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.chip {
  display: inline-block;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.chip-on { background: #d8ecec; color: #205858; }
.chip-share { background: #f6e6d6; color: #7a4a20; }
.chip-off { background: #eee; color: #666; }
.chip-nights { background: #ece1f5; color: #4a2b6b; }
.chip-early { background: #fff0e0; color: #8a4a10; }
.chip-comp { background: #fde3ea; color: #a3253d; }
.chip-faculty { background: #fff4d1; color: #7a5a15; }
.pack-section {
  margin-top: 8px;
}
.pack-section-head {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #888;
  font-weight: 700;
  margin-bottom: 4px;
}
.pack-section-universal .pack-section-head {
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  background: #f5f0e2;
  color: #7a6828;
  display: inline-block;
  list-style: none;
}
.pack-section-universal .pack-section-head::-webkit-details-marker { display: none; }
.pack-section-universal .pack-items {
  margin-top: 4px;
  opacity: .85;
}
.pack-section-universal .pack-item-label {
  font-weight: 500;
  color: #555;
  font-size: 13px;
}
.pack-items {
  list-style: none;
  padding: 0;
  margin: 0;
}
.pack-item {
  padding: 3px 0;
}
.pack-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  line-height: 1.4;
}
.pack-toggle {
  display: flex;
  gap: 3px;
  margin-top: 1px;
  flex-shrink: 0;
}
.tog {
  border: 1px solid #ccc;
  background: #fff;
  color: #999;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .12s;
}
.tog:hover:not(:disabled) { border-color: #888; color: #222; }
.tog:disabled { cursor: default; opacity: .5; }
.tog-packed.is-on { background: #4a9c56; border-color: #3e8548; color: #fff; }
.tog-missing.is-on { background: #d94a3a; border-color: #b73a2b; color: #fff; }
.pack-item-packed .pack-item-label { color: #4a9c56; text-decoration: line-through; text-decoration-color: rgba(74,156,86,.5); }
.pack-item-missing .pack-item-label { color: #b73a2b; font-weight: 700; }
.pack-item-missing { background: #fdecea; border-radius: 4px; padding-left: 4px; padding-right: 4px; }
.pack-line {
  flex: 1;
  min-width: 0;
}
.pack-item-label {
  font-weight: 600;
  color: #1a1a1a;
}
.pack-item-detail {
  display: block;
  color: #555;
  font-size: 13px;
  margin-top: 1px;
  word-wrap: break-word;
}
.pack-line.is-note .pack-item-label {
  color: #a3253d;
  font-weight: 700;
  font-size: 13px;
}
</style>
